import { ProgressiveImage } from '../ProgressiveImage'
import { useState, type ImgHTMLAttributes, type SyntheticEvent } from 'react'

type SmartPropertyImageFit = 'cover' | 'contain'
type SmartPropertyImageTone = 'card' | 'detail' | 'thumb' | 'media'

type SmartPropertyImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  fit?: SmartPropertyImageFit
  tone?: SmartPropertyImageTone
}

function getImageShape(width: number, height: number) {
  if (!width || !height) return 'unknown'
  const ratio = width / height
  if (ratio <= 0.72) return 'portrait'
  if (ratio <= 0.94) return 'soft-portrait'
  if (ratio >= 2.15) return 'wide'
  if (ratio >= 1.42) return 'landscape'
  return 'square'
}

export function SmartPropertyImage({
  className = '',
  fit = 'cover',
  loading = 'lazy',
  onLoad,
  style,
  tone = 'card',
  ...props
}: SmartPropertyImageProps) {
  const [shape, setShape] = useState('unknown')

  const handleLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget
    setShape(getImageShape(image.naturalWidth, image.naturalHeight))
    onLoad?.(event)
  }

  return (
    <ProgressiveImage
      {...props}
      className={[
        'property-smart-image',
        `is-${shape}`,
        `is-fit-${fit}`,
        `is-tone-${tone}`,
        className,
      ].filter(Boolean).join(' ')}
      decoding={props.decoding || 'async'}
      loading={loading}
      onLoad={handleLoad}
      style={{
        ...style,
        height: style?.height || '100%',
        objectFit: fit,
        objectPosition: '50% 50%',
        width: style?.width || '100%',
      }}
    />
  )
}
